// Platform helpers for offer filtering.
// Torox offers carry `devices` like ["android"], ["ios"], ["*"].
// Rule: desktop web shows all (QR fallback exists). iOS sees iOS-only, Android sees Android-only.

export const PLATFORMS = {
  IOS: 'ios',
  ANDROID: 'android',
  DESKTOP: 'desktop',
};

// Manual desktop filter options. 'all' shows everything (desktop default);
// 'desktop' means desktop-only (windows/macos). Kept separate from PLATFORMS
// so auto-filter (mobile) behavior stays unchanged.
export const DEVICE_FILTERS = {
  ALL: 'all',
  ANDROID: 'android',
  IOS: 'ios',
  DESKTOP: 'desktop',
};

const IOS_DEVICES = new Set(['iphone', 'ipad', 'ios']);
const ANDROID_DEVICES = new Set(['android']);
const DESKTOP_DEVICES = new Set(['windows', 'macos']);

export function getUserPlatform(userAgent) {
  const ua =
    userAgent ||
    (typeof navigator !== 'undefined' ? navigator.userAgent : '') ||
    '';
  if (/android/i.test(ua)) return PLATFORMS.ANDROID;
  if (/iPad|iPhone|iPod/i.test(ua)) return PLATFORMS.IOS;
  return PLATFORMS.DESKTOP;
}

export function normalizeDevices(devices) {
  if (!Array.isArray(devices)) return [];
  return devices.map((d) => String(d).toLowerCase());
}

export function isOfferCompatible(devices, platform = PLATFORMS.DESKTOP) {
  if (platform === PLATFORMS.DESKTOP) return true;
  const normalized = normalizeDevices(devices);
  if (normalized.length === 0 || normalized.includes('*')) return true;
  if (platform === PLATFORMS.IOS) {
    return normalized.some((d) => IOS_DEVICES.has(d));
  }
  if (platform === PLATFORMS.ANDROID) {
    return normalized.some((d) => ANDROID_DEVICES.has(d));
  }
  return true;
}

export function filterOffersByPlatform(offers, platform = PLATFORMS.DESKTOP) {
  if (!Array.isArray(offers)) return offers;
  if (platform === PLATFORMS.DESKTOP) return offers;
  return offers.filter((offer) => isOfferCompatible(offer?.devices, platform));
}

// Manual single-select filter for desktop UI. 'all' returns everything;
// 'desktop' matches windows/macos (plus '*' / empty = universal offers).
export function filterOffersByDeviceFilter(offers, selection = DEVICE_FILTERS.ALL) {
  if (!Array.isArray(offers)) return offers;
  if (!selection || selection === DEVICE_FILTERS.ALL) return offers;
  return offers.filter((offer) => {
    const normalized = normalizeDevices(offer?.devices);
    if (normalized.length === 0 || normalized.includes('*')) return true;
    if (selection === DEVICE_FILTERS.IOS) {
      return normalized.some((d) => IOS_DEVICES.has(d));
    }
    if (selection === DEVICE_FILTERS.ANDROID) {
      return normalized.some((d) => ANDROID_DEVICES.has(d));
    }
    if (selection === DEVICE_FILTERS.DESKTOP) {
      return normalized.some((d) => DESKTOP_DEVICES.has(d));
    }
    return true;
  });
}
